import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { techRoadmap, toggleRoadmapTopic } from "@/lib/api";
import { Loader2, Check, ArrowRight, Map, Clock } from "lucide-react";

/* Compact "My Plan" card — the ONE placement plan, embedded on the Dashboard,
   Resume and Interview pages. Shows progress + the next few unticked topics and
   links to the full plan (Coach → My Plan). Reads the same /roadmap the full
   page uses, so every surface agrees. */

export default function CoachingPlan({ compact = false, limit }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(null);
  useEffect(() => { techRoadmap().then(({ data }) => setData(data)).catch(() => setErr(true)); }, []);

  const toggle = async (id) => {
    setBusy(id);
    try { const { data: next } = await toggleRoadmapTopic(id); setData(next); } catch (_) {} finally { setBusy(null); }
  };

  if (err) return null;
  if (!data) return (
    <div className="rounded-3xl bg-white border border-stone-200 p-6 flex items-center gap-3 text-stone-500 text-sm">
      <Loader2 size={16} className="animate-spin text-[#C2410C]" /> Loading your plan… (first time takes ~20s)
    </div>
  );

  const next = data.phases.flatMap((p) => p.topics.filter((t) => !t.done).map((t) => ({ ...t, phase: p.title }))).slice(0, limit || (compact ? 3 : 5));
  const { progress } = data;

  return (
    <div className="rounded-3xl bg-white border border-stone-200 p-6" data-testid="plan-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-stone-600 inline-flex items-center gap-2"><Map size={15} className="text-[#C2410C]" /><span className="font-semibold text-stone-900">{progress.completed}</span> of {progress.total} steps done · <Clock size={12} /> ~{progress.hours_left}h left</p>
        <Link to="/app/coach?tab=plan" className="text-sm font-medium text-[#C2410C] hover:text-stone-900 inline-flex items-center gap-1">Open My Plan <ArrowRight size={14} /></Link>
      </div>
      <div className="mt-3 h-2 rounded-full bg-stone-100 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#C2410C] to-amber-500 transition-all" style={{ width: `${progress.percent}%` }} />
      </div>
      {next.length > 0 ? (
        <div className="mt-4 space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">Up next</p>
          {next.map((t) => (
            <div key={t.topic_id} className="flex items-start gap-3 rounded-2xl border border-stone-200 px-3.5 py-2.5">
              <button onClick={() => toggle(t.topic_id)} disabled={busy === t.topic_id} aria-label="Mark done"
                className="shrink-0 mt-0.5 w-5 h-5 rounded-md border-2 border-stone-300 hover:border-stone-900 flex items-center justify-center">
                {busy === t.topic_id ? <Loader2 size={10} className="animate-spin text-stone-400" /> : null}
              </button>
              <div className="min-w-0">
                <p className="text-sm font-medium text-stone-900 truncate">{t.title}</p>
                <p className="text-xs text-stone-500">{t.phase} · ~{t.est_hours}h</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-4 text-sm text-emerald-700 inline-flex items-center gap-1.5"><Check size={14} /> Every step done — you're interview-ready.</p>
      )}
    </div>
  );
}
